import React, { useState, useEffect, useContext, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, IconButton, Slider, Stack } from "@mui/material";
import { start } from "../../action/streamSong";
import SkipPreviousRoundedIcon from "@mui/icons-material/SkipPreviousRounded";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseOutlinedIcon from "@mui/icons-material/PauseOutlined";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import VolumeDownOutlinedIcon from "@mui/icons-material/VolumeDownOutlined";
import VolumeOffOutlinedIcon from "@mui/icons-material/VolumeOffOutlined";
import { SocketContext } from "../../context/socket";
import { getAudienceId, getUserId, putStreamRecord } from "../../constants/function";
import { startSeek, endSeek } from "../../action/seek";

export default function StreamPlayer({
  handleFile,
  roomId,
  onLoader,
  liveUsers,
}) {
  const dispatch = useDispatch();
  const seek = useSelector((state) => state.seek);
  const { index, prevIndex, songs, pos, track, timeStamp, vol } = useSelector(
    (state) => state.streamSong
  );
  const [progress, setProgress] = useState(0);
  const userId = getUserId();
  const [volume, setVolume] = useState(100);
  const [showVolume, setShowVolume] = useState(false);
  const { Socket } = useContext(SocketContext);

  useEffect(() => {
    setProgress(pos);
  }, [pos]);

  useEffect(() => {
    setVolume(vol);
  }, [vol]);

  const duration = track?.duration || 0;

  const handleSeek = useCallback(
    (value, end, index, track) => {
      if (!seek?.started && !end) {
        putRecord(
          "seekStart",
          songs[index]._id,
          roomId,
          index,
          track.getCurrentTime(),
          new Date().getTime(),
          songs
        );
        dispatch(startSeek(value)); // Dispatch startSeek action
      } else if (seek?.started && end) {
        putRecord(
          "seekStop",
          songs[index]._id,
          roomId,
          index,
          track.getCurrentTime(),
          new Date().getTime(),
          songs
        );
        dispatch(endSeek()); // Dispatch endSeek action
      }
    },
    [seek, track, songs, roomId, dispatch]
  );

  const handleEnd = (e, value) => {
    handleSeek(value, true, index, track);
  };

  const handleChange = (e, newProgress) => {
    handleSeek(newProgress, false, index, track);
    const audioFileTrack = track;
    const cT = Math.floor(audioFileTrack.getCurrentTime());
    const du = Math.floor(audioFileTrack.duration);
    const prg = (cT * 100) / du;
    console.log(newProgress);
    setProgress(newProgress);
    audioFileTrack.seekAudioBuffer(newProgress);
    localStorage.setItem("streamIndex", index);
    dispatch(
      start({
        index,
        songs,
        prevIndex,
        pos: newProgress,
        track: audioFileTrack,
      })
    );
  };

  const playPause = () => {
    const audioFileTrack = track;
    if (audioFileTrack.currentState === "playing") {
      audioFileTrack.pauseProcessAudioBuffer();
      localStorage.setItem("streamIndex", -1);
      if (Socket) {
        Socket.emit("song-start", {
          roomId,
          ownerId: userId,
          type: "pause",
          _id: songs[index]._id,
        });
      }
      putRecord(
        "pause",
        songs[index]._id,
        roomId,
        index,
        track.getCurrentTime(),
        new Date().getTime(),
        songs
      );
    } else if (audioFileTrack.currentState === "stopped") {
      Socket.emit("song-start", {
        roomId,
        ownerId: userId,
        type: "stop",
        _id: songs[index]._id,
      });
      handleFile(track, songs, index, index, true, vol);
    } else {
      audioFileTrack.resumeProcessAudioBuffer();
      localStorage.setItem("streamIndex", index);
      if (Socket) {
        Socket.emit("song-start", {
          song: songs[index].title,
          _id: songs[index]._id,
          thumbnailImg: songs[index].thumbnailImg,
          ownerId: userId,
          roomId,
          type: "start",
          artist: songs[index]?.artist,
        });
      }
      putRecord(
        "start",
        songs[index]._id,
        roomId,
        index,
        track.getCurrentTime(),
        new Date().getTime(),
        songs
      );
    }
    dispatch(
      start({
        index,
        songs,
        prevIndex,
        pos,
        track: audioFileTrack,
      })
    );
  };

  const handleNext = async (type, currentIndex) => {
    if (type) {
      onLoader(true);
      const newIndex = type === "next" ? currentIndex + 1 : currentIndex - 1;
      const response = await fetchWithProgress(songs[newIndex].fileId?.filePath);
      const file = await response.blob();
      putRecord(
        type === "next" ? "next" : "previous",
        songs[newIndex]._id,
        roomId,
        newIndex,
        0,
        new Date().getTime(),
        songs
      );
      Socket.emit("song-start", {
        roomId,
        ownerId: userId,
        type: "stop",
        _id: songs[index]._id,
      });
      handleFile(file, songs, newIndex, index, false, vol, track);
    }
  };

  const fetchWithProgress = async (url) => {
    const response = await fetch(url);
    const reader = response.body.getReader();
    const stream = new ReadableStream({
      start(controller) {
        const pump = () =>
          reader.read().then(({ done, value }) => {
            if (done) {
              controller.close();
              return;
            }
            controller.enqueue(value);
            const loaded = value.length;
            const total = +response.headers.get("Content-Length");
            const percentComplete = (loaded / total) * 100;
            console.log(`Downloaded ${percentComplete}%`);
            return pump();
          });
        return pump();
      },
    });

    return new Response(stream, response);
  };

  const handleVolume = (value) => {
    if (track) {
      const s = track; //temporary song array
      s.setVolume(value);
      setVolume(value);
      dispatch(
        start({
          index,
          songs,
          prevIndex,
          pos,
          track: s,
          vol: value,
        })
      );
    }
  };

  const putRecord = async (
    event,
    musicId,
    playlistId,
    index,
    timeInSong,
    associationId,
    playlistSongs
  ) => {
    const userId = getUserId();
    const audienceArray = await getAudienceId(liveUsers);
    const payload = {
      eventType: event,
      timestamp: new Date().getTime(),
      userId,
      musicId,
      playlistId,
      livestreamId: playlistId,
      songDetails: playlistSongs[index],
      timeInSong,
      associationId,
      audienceArray,
    };
    putStreamRecord(payload);
  };

  if (index < 0) return null;

  return (
    <Box
      onClick={() => setShowVolume(false)}
      sx={{
        position: "absolute",
        width: "inherit",
        bottom: "0",
        background: "#1e1e1e",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        height: "55px",
      }}
    >
      <Slider
        size="small"
        value={progress}
        aria-label="Small"
        valueLabelDisplay="auto"
        max={duration}
        onChange={(e, value) => handleChange(e, value)}
        onChangeCommitted={handleEnd}
        sx={{
          padding: 0,
          height: 5,
          position: "absolute",
          top: 0,
          "& .MuiSlider-thumb": {
            width: 8,
            height: 8,
            transition: "0.3s cubic-bezier(.47,1.64,.41,.8)",
            "&:before": {
              boxShadow: "0 2px 12px 0 rgba(0,0,0,0.4)",
            },

            "&.Mui-active": {
              width: 14,
              height: 14,
            },
          },
          "& .MuiSlider-rail": {
            opacity: 0.28,
          },
        }}
      />
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-evenly"
        sx={{ height: "50px", background: "#1e1e1e" }}
      >
        <IconButton
          aria-label="prev song"
          disabled={index === 0}
          onClick={() =>
            songs.length > 1 && index >= 1
              ? handleNext("prev", index)
              : handleNext()
          }
        >
          <SkipPreviousRoundedIcon
            sx={{
              fontSize: "22px",
              color: index === 0 ? "gray" : "#fff",
            }}
            color="default"
            className="_cp"
          />
        </IconButton>

        <IconButton
          aria-label="play song"
          sx={{ backgroundColor: "#2E2E2D" }}
          onClick={() => playPause(index)}
        >
          {track.currentState !== "playing" ? (
            <PlayArrowRoundedIcon
              sx={{ fontSize: "22px" }}
              color="default"
              className="_cp"
            />
          ) : (
            <PauseOutlinedIcon
              sx={{ fontSize: "22px" }}
              color="default"
              className="_cp"
            />
          )}
        </IconButton>

        <IconButton
          aria-label="next song"
          disabled={index === songs.length - 1}
          onClick={() =>
            songs.length > 1 && index < songs.length - 1
              ? handleNext("next", index)
              : handleNext()
          }
        >
          <SkipNextRoundedIcon
            className="_cp"
            sx={{
              fontSize: "22px",
              color: index === songs.length - 1 ? "gray" : "#fff",
            }}
          />
        </IconButton>
        <Box sx={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
  {showVolume ? (
    <Box className="__volume" sx={{ position: "absolute", bottom: "0", }}>
      <Slider
        aria-label="Volume"
        orientation="vertical"
        value={volume}
        max={50}
        step={1}
        marks={[
          { value: 0 },
          { value: 10 },
          { value: 20 },
          { value: 30 },
          { value: 40 },
          { value: 50 },
        ]}
        defaultValue={10}
        onChange={(e, value) => {
          console.log(`Volume: ${value}`); // Logs the current volume to console
          handleVolume(value >= 1 ? value : 1); // Ensures the minimum volume is 1
        }}
        sx={{
          height: "200px",  // Adjust the height of the slider
          width: "6px",     // Adjust the width of the slider
          "& .MuiSlider-thumb": {
            width: 12,      // Adjust the size of the thumb
            height: 12,     // Adjust the size of the thumb
            transition: "0.3s cubic-bezier(.47,1.64,.41,.8)",
            "&:before": {
              boxShadow: "0 2px 12px 0 rgba(0,0,0,0.4)",
            },
          },
          "& .MuiSlider-track": {
            width: "6px",  // Adjust the width of the track
          },
          "& .MuiSlider-rail": {
            width: "6px",  // Adjust the width of the rail
          },
        }}
      />
    </Box>
  ) : null}


          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (volume === 0) {
                setShowVolume(true);
                handleVolume(50);
              } else {
                setShowVolume(!showVolume);
              }
            }}
          >
            {volume === 0 ? (
              <VolumeOffOutlinedIcon
                sx={{ fontSize: "22px" }}
                className={showVolume ? "__volume_active" : "__volume_button"}
              />
            ) : volume > 0 && volume < 50 ? (
              <VolumeDownOutlinedIcon
                sx={{ fontSize: "22px" }}
                className={showVolume ? "__volume_active" : "__volume_button"}
              />
            ) : (
              <VolumeUpOutlinedIcon
                sx={{ fontSize: "22px" }}
                className={showVolume ? "__volume_active" : "__volume_button"}
              />
            )}
          </IconButton>
        </Box>
      </Stack>
    </Box>
  );
}
